import { ActiveSecurityContextStateService } from '@security/active-security/active-security-context.state-service';

import { userRoutes } from '@security/users/user-routes';
import { organisationRoutes } from '@organisations/organisation-routes';
import { customReportTypesRoutes } from '@app/custom-reports-admin/custom-report-types-routes';
import { integrationsRoutes } from '@app/integrations-admin/integrations-routes';
import { HashMap } from '@datorama/akita';
import { FeatureFlagService, FeatureFlagsServiceInjectionToken, FeatureFlagType } from '@feature-flags/feature-flag.service';
import { cintraHrLogRoutes } from '@app/hrlog/cintrahrlog-routes';
import { designSystemRoutes } from '@app/design-system/design-system-routes';
import { Member } from '@security/users/models/member';
import { PaymentAuthorisationCountStateService } from '@app/payments-hub/payment-authorisations/shared/state/payment-authorisation-count.state-service';
import { map, switchMap } from 'rxjs/operators';
import { Inject, Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, catchError, combineLatest, Observable, of, Subscription } from 'rxjs';
import { MenuItem, TopMenuItem } from '../../../../component-library/layout/menu-item/menu-interfaces';
import { SidebarMenuConfig } from '../../../../component-library/layout/sidebar/sidebar-menu.interface';

@Injectable({ providedIn: 'root' })
export class CintraPayNavigationService implements OnDestroy {
  private navigationUpdated$ = new BehaviorSubject<boolean | undefined>(undefined);
  private paymentCountSubscription: Subscription | null = null;

  paymentLink: MenuItem = {
    label: 'Payments',
    route: 'payments-hub/authorisations',
    feature: 'Payments',
    icon: 'bank',
    counter: 0,
    type: 'MenuLink'
  } as any;

  constructor(
    public activeSecurity: ActiveSecurityContextStateService,
    @Inject(FeatureFlagsServiceInjectionToken) private featureFlagService: FeatureFlagService,
    private paymentAuthorisationCountStateService: PaymentAuthorisationCountStateService
  ) {}

  getSidebarMenuConfig$(): Observable<SidebarMenuConfig> {
    return combineLatest([
      this.featureFlagService.getAllValues$(),
      this.activeSecurity.activeMembership$,
      this.activeSecurity.intelliHrEnabled$
    ]).pipe(switchMap(([featureFlags, member, intelliHrEnabled]) => this.buildSidebarMenuConfig(featureFlags, member, intelliHrEnabled)));
  }

  private buildSidebarMenuConfig = (
    featureFlags: HashMap<FeatureFlagType>,
    member: Member,
    intelliHrEnabled: boolean
  ): Observable<SidebarMenuConfig> => {
    return new Observable<SidebarMenuConfig>((observer) => {
      const emitSidebarConfig = () => {
        const sidebarConfig = this.getSidebarMenuConfig(featureFlags, member, intelliHrEnabled);
        observer.next(sidebarConfig);
      };

      emitSidebarConfig();

      const subscription = this.navigationUpdated$.subscribe((shouldEmit) => {
        if (shouldEmit) {
          emitSidebarConfig();
        }
      });

      return () => subscription.unsubscribe();
    });
  };
  private getSidebarMenuConfig = (featureFlags: HashMap<FeatureFlagType>, member: Member, intelliHrEnabled: boolean): SidebarMenuConfig => {
    const sections: TopMenuItem[] = [];
    const mainItems: MenuItem[] = [];

    if (this.activeSecurity.hasOrganisationAuthorityTo('AccessPayrolls')) {
      mainItems.push({
        label: 'Payrolls',
        route: 'employers',
        icon: 'gb-pound',
        execute: (data?: any) => this.activeSecurity.navigatePayrolls(),
        type: 'MenuLink',
        feature: 'Payrolls'
      } as any);
    }

    if (this.activeSecurity.hasOrganisationAuthorityTo('AccessGroups') && this.activeSecurity.activeMembership.groupsEnabled) {
      mainItems.push({
        label: 'Groups',
        route: 'access-control/groups',
        icon: 'users',
        type: 'MenuLink',
        feature: 'Groups'
      } as any);
    }

    if (
      this.activeSecurity.hasOrganisationAuthorityTo('AccessPayrollReports') ||
      this.activeSecurity.hasOrganisationAuthorityTo('AccessReporting')
    ) {
      mainItems.push({
        label: 'Reports',
        route: 'reports',
        icon: 'chart-line',
        type: 'MenuLink',
        feature: 'Reports'
      } as any);
    }

    if (this.activeSecurity.hasOrganisationAuthorityTo('AccessMessaging') && this.activeSecurity.activeMembership.messagingEnabled) {
      mainItems.push({
        label: 'Messaging',
        route: 'messaging/inbox',
        icon: 'chat-circle',
        type: 'MenuLink',
        feature: 'Messaging'
      } as any);
    }

    if (featureFlags['pensionConnect'] === true && this.activeSecurity.hasBureauProfileAuthorityTo('IsBureauUser')) {
      mainItems.push({
        label: 'Pensions connect',
        route: 'pension-connect/dashboard',
        icon: 'link',
        type: 'MenuLink',
        feature: 'Pension Connect'
      } as any);
    }

    const shouldShowPaymentsLink =
      featureFlags['payrollPaymentsPage'] === true &&
      this.activeSecurity.hasOrganisationAuthorityTo('ViewPayDetails') &&
      this.activeSecurity.hasOrganisationAuthorityTo('AccessPayrolls') &&
      this.activeSecurity.activeMembership.payrollSettings?.length > 0;

    if (shouldShowPaymentsLink) {
      this.setupPaymentCountSubscription();

      mainItems.push({
        ...this.paymentLink,
        counter: this.paymentLink.counter,
        type: 'MenuLink'
      } as any);
    } else {
      this.cleanupPaymentCountSubscription();
    }

    if (this.activeSecurity.hasOrganisationAuthorityTo('AccessPayrolls') && this.activeSecurity.activeMembership.cintraIqAuroraEnabled) {
      const cloudIQURL = 'https://payrollengine.cintra.co.uk/RDWeb/webclient';

      mainItems.push({
        label: 'Cloud IQ',
        route: cloudIQURL,
        openInNewTab: true,
        icon: 'cloud',
        type: 'MenuLink',
        feature: 'Cloud IQ'
      } as any);
    }

    if (this.activeSecurity.hasOrganisationAuthorityTo('AccessPayrolls') && intelliHrEnabled) {
      mainItems.push({
        label: 'CintraHR log',
        route: cintraHrLogRoutes.hub,
        icon: 'file-text',
        type: 'MenuLink',
        feature: 'CintraHrLog'
      } as any);
    }

    let settings = [];

    if (this.activeSecurity.hasOrganisationAuthorityTo('EditUsers')) {
      settings.push({
        label: 'Manage users',
        route: userRoutes.hub,
        feature: 'Settings',
        section: 'ManageUsers'
      });
    }

    if (
      this.activeSecurity.hasFeaturePackAuthorityTo('AccessPaySpines') &&
      this.activeSecurity.hasOrganisationAuthorityTo('AccessPositionManagement')
    ) {
      settings.push({
        label: 'Position management',
        route: 'positions/hub',
        feature: 'Settings',
        section: 'PositionManagement'
      });
    }

    if (this.activeSecurity.hasApplicationAuthorityTo('EditCintraEmployees')) {
      settings.push({
        label: 'Manage Cintra users',
        route: 'users/cintra-hub',
        feature: 'Settings',
        section: 'ManageCintraUsers'
      });
    }

    if (this.activeSecurity.hasApplicationAuthorityTo('EditOrganisations')) {
      settings.push(
        {
          label: 'Manage organisations',
          route: organisationRoutes.hub,
          feature: 'Settings',
          section: 'ManageOrganisations'
        },
        {
          label: 'Cintra Admin Area',
          route: 'admin-area/hub',
          feature: 'Settings',
          section: 'CintraAdminArea'
        }
      );
    }

    if (this.activeSecurity.hasApplicationAuthorityTo('CreateCustomReports')) {
      settings.push({
        label: 'Manage Custom Reports',
        route: customReportTypesRoutes.hub,
        feature: 'Settings',
        section: 'ManageCustomReports'
      });
    }

    if (this.activeSecurity.hasOrganisationAuthorityTo('ManageIntegrations')) {
      settings.push({
        label: 'Manage integrations',
        route: integrationsRoutes.hub,
        feature: 'Settings',
        section: 'ManageIntegrations'
      });
    }

    if (this.activeSecurity.isCintraAdmin()) {
      settings.push({
        label: 'Design system',
        route: designSystemRoutes.home,
        feature: 'Settings',
        featureFlagBooleanValue: 'on',
        section: 'DesignSystem'
      });
    }

    if (
      this.activeSecurity.hasOrganisationAuthorityTo('EquifaxDataConsent') ||
      (featureFlags['sso'] === true && this.activeSecurity.hasOrganisationAuthorityTo('EditSingleSignOn'))
    ) {
      settings.push({
        label: 'Company Settings',
        route: 'customer/settings',
        feature: 'Settings',
        section: 'CustomerSettings'
      });
    }

    if (featureFlags['cintraHr'] === true && this.activeSecurity.hasApplicationAuthorityTo('EditOrganisations'))
      settings.push({
        label: 'Manage HR',
        route: 'hr/tenants',
        feature: 'Settings',
        section: 'ManageHrTenants',
        featureFlag: 'cintraHr'
      });

    if (settings.length > 0) {
      const settingsItems: MenuItem[] = settings.map(
        (setting) =>
          ({
            label: setting.label,
            route: setting.route,
            icon: setting.icon,
            execute: setting.execute,
            type: 'MenuLink',
            feature: setting.feature,
            section: setting.section
          }) as any
      );

      sections.push({
        title: 'Settings',
        icon: 'gear',
        collapsible: true,
        collapsed: true,
        items: settingsItems
      });
    }

    if (mainItems.length > 0) {
      sections.unshift({
        items: mainItems
      });
    }

    return {
      sections,
      bottomNavItems: this.fetchBottomNavItems()
    };
  };

  private setupPaymentCountSubscription(): void {
    if (!this.paymentCountSubscription) {
      this.paymentCountSubscription = this.paymentAuthorisationCountStateService.paymentAuthorisationCount$
        .pipe(
          map((paymentAuthCount) => {
            const counter = paymentAuthCount?.totalCount || 0;
            this.paymentLink.counter = counter;
            this.navigationUpdated$.next(true);
          }),
          catchError(() => {
            this.paymentLink.counter = 0;
            this.navigationUpdated$.next(true);
            return of(null);
          })
        )
        .subscribe();
    }
  }

  private cleanupPaymentCountSubscription(): void {
    if (this.paymentCountSubscription) {
      this.paymentCountSubscription.unsubscribe();
      this.paymentCountSubscription = null;
      this.paymentLink.counter = 0;
      this.navigationUpdated$.next(true);
    }
  }

  private fetchBottomNavItems = (): MenuItem[] => {
    const bottomNavItems: MenuItem[] = [];

    if (this.activeSecurity.hasOrganisationAuthorityTo('AccessPayrolls')) {
      bottomNavItems.push({
        label: 'Home',
        route: 'employers',
        icon: 'house',
        feature: 'Payrolls',
        type: 'MenuLink',
        execute: (data?: any) => this.activeSecurity.navigatePayrolls()
      } as any);
    }

    if (
      this.activeSecurity.hasOrganisationAuthorityTo('AccessPayrollReports') ||
      this.activeSecurity.hasOrganisationAuthorityTo('AccessReporting')
    ) {
      bottomNavItems.push({
        label: 'Reports',
        route: 'reports',
        icon: 'chart-line',
        feature: 'Reports',
        type: 'MenuLink'
      } as any);
    }

    return bottomNavItems;
  };

  ngOnDestroy(): void {
    this.cleanupPaymentCountSubscription();
  }
}
